import React, { useState } from 'react';
import {
  Badge,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Popover,
  CircularProgress,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsNone as NotificationsNoneIcon,
  AccessTime as AccessTimeIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { markOneAsReadStore, setNotificationsList } from '../../store/notifications';
import { alpha } from '@mui/material/styles';
import { formatDateToYmdNumeric } from '../../commons/utils';
import { useNavigate } from 'react-router-dom';

export const NotificationIcon = () => {

  // NAVIGATE 
  const navigate = useNavigate()


  //  REDUX
  const { notificationsList }:any  = useSelector( (state:any) => state.notifications );
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any>([]);
  const [isSending, setIsSending] = useState<string | null>(null);

  React.useEffect(() => {
    setNotifications(notificationsList)
  }, [notificationsList]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = async(id: string) => {
    setIsSending(id);
    const response = await dispatch( markOneAsReadStore(id) );
    setIsSending(null);
    const updatedNotifications = notifications.map((notification:any) => 
      notification.id === id ? { ...notification, read: true } : notification
    );
    setNotifications(updatedNotifications);
    dispatch(setNotificationsList(updatedNotifications));
  };

  const unreadCount = notifications?.filter( (n:any) => !n.read).length || 0;
  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <IconButton 
        color="inherit" 
        sx={{ right: 15 }}
        aria-label="notifications"
        onClick={handleClick}
      >
        <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontWeight: 800 } }}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            width: 380,
            overflow: 'visible',
            filter: 'drop-shadow(0px 8px 24px rgba(0,0,0,0.12))',
            borderRadius: '16px',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 28,
              width: 12,
              height: 12,
              bgcolor: '#ffffff',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ p: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#ffffff', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#2e263d', fontSize: '1.1rem' }}>
            Notificaciones 
          </Typography>
          {unreadCount > 0 && (
            <Chip 
              label={`${unreadCount} nuevas`} 
              size="small" 
              sx={{ bgcolor: alpha('#FF4C51', 0.1), color: '#FF4C51', fontWeight: 800, fontSize: '0.75rem', height: 24 }} 
            />
          )}
        </Box>

        <List sx={{ 
          p: 0,
          overflowY: 'auto',
          maxHeight: 420,
          bgcolor: '#ffffff',
          borderBottomLeftRadius: '16px',
          borderBottomRightRadius: '16px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#e0e0e0', borderRadius: '4px' }
        }}>
          {notifications?.length > 0 ? (
            notifications.map((notification:any) => (
              <Box key={notification.id} sx={{ position: 'relative' }}>
                <ListItem
                  sx={{
                    px: 3,
                    py: 2,
                    backgroundColor: notification.read ? 'transparent' : alpha('#4A148C', 0.03),
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha('#4A148C', 0.06),
                    }
                  }}
                >
                  {!notification.read && (
                    <Box sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <CircleIcon sx={{ fontSize: 8, color: '#FF4C51' }} />
                    </Box> 
                  )}
                  <Avatar onClick={() => {
                    if (notification.ratingId) {
                      navigate(`/calificaciones/gestion-calificaciones`, { state: { ratingId: notification.ratingId } });
                      handleClose();
                    }
                  }}  sx={{ bgcolor: notification.read ? '#f5f5f5' : alpha('#4A148C', 0.1), color: notification.read ? '#9e9e9e' : '#4A148C', width: 40, height: 40, mr: 2, cursor: notification.ratingId ? 'pointer' : 'default' }}>
                    <NotificationsNoneIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  
                  <ListItemText
                    primary={notification.text}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>{ formatDateToYmdNumeric(notification.time)}</Typography>
                      </Box>
                    }
                    primaryTypographyProps={{
                      fontWeight: notification.read ? 500 : 700,
                      color: notification.read ? 'text.secondary' : '#2e263d',
                      fontSize: '0.85rem',
                      lineHeight: 1.3
                    }}
                  />

                  {!notification.read && (
                    <Tooltip title="Marcar como leída" placement="left">
                      <IconButton
                        size="small"
                        onClick={() => markAsRead(notification.id)}
                        sx={{ ml: 1, color: '#4A148C', bgcolor: alpha('#4A148C', 0.05), '&:hover': { bgcolor: alpha('#4A148C', 0.15) } }}
                      >
                        { isSending === notification.id ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 18 }} /> }
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
                <Divider sx={{ mx: 2, borderColor: alpha('#000000', 0.04) }} />
              </Box>
            ))
          ) : (
            <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: '#f5f5f5', mb: 2 }}>
                <NotificationsNoneIcon sx={{ fontSize: 32, color: '#bdbdbd' }} />
              </Avatar>
              <Typography variant="subtitle2" sx={{ color: '#2e263d', fontWeight: 700, mb: 0.5 }}>Todo al día</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>No tienes notificaciones pendientes por leer.</Typography>
            </Box>
          )}
        </List>
      </Popover>
    </>
  );
};

export default NotificationIcon;